import { LOCATION_CHANGE } from 'connected-react-router';
import { REHYDRATE } from 'redux-persist';
import { createTracker, EventTypes } from 'redux-segment';

import { makePathMatch } from 'src/app/popsicle/utils/segment';
import * as paths from 'src/app/router/paths';
import routes from 'src/app/router/routes';
import * as actionTypes from 'src/app/state/actions/types';
import { SESSION_STORAGE_KEYS } from 'src/app/storage/sessionStorageKeys';
import { getSessionStorageItem } from 'src/app/utils';

interface CreateIdentifyEventParams {
  userId?: string;
}

const createIdentityEvent = ({ userId, ...traits }: CreateIdentifyEventParams) => {
  return {
    eventType: EventTypes.identify,
    eventPayload: {
      /**
       * We routinely identify users to Segment at the same time that we identify users to consumer-bff
       * (for the sake of Optimizely). When we identify users to Segment, we should NOT be identifying
       * user IDs that have a uuid shape because those represent anonymous users and Segment tracks
       * that on thier own.
       *
       * TODO: Remove this tweak and separate optimizely/bff logic from segment.
       */
      userId: userId === undefined || `${userId}`.includes('-') ? undefined : userId,
      traits,
    },
  };
};

// redux-segment mapper
const customMapper = {
  mapper: {
    skipDefaultMapping: true,
    [actionTypes.SEGMENT_PAGE]: () => {
      const pathMatch = makePathMatch({ routes, paths });
      const match: any = pathMatch(window.location.pathname);
      return {
        eventType: EventTypes.page,
        eventPayload: {
          name: match.name,
          properties: {
            params: match.params,
          },
        },
      };
    },
    /**
     * @WARN async callbacks in this redux-segment mapper will not be able to send
     * "event objects", because of the nature of the library internals.
     *
     * @NOTE however, we CAN  dispatch a new SEGMENT_(PAGE|TRACK) action here as
     * a "side effect". That action will get passed down through middleware, and
     * end up somewhere here.
     */
    [LOCATION_CHANGE]: () => {
      const referrer = getSessionStorageItem(SESSION_STORAGE_KEYS.externalReferrer);
      const pathMatch = makePathMatch({ routes, paths });
      const match: any = pathMatch(window.location.pathname);

      return {
        eventType: EventTypes.page,
        eventPayload: {
          name: match.name,
          properties: {
            referrer,
            params: match.params,
          },
        },
      };
    },
    /**
     * @deprecated
     */
    [actionTypes.SEGMENT_CLICK]: (getState, action) => ({
      eventType: EventTypes.track,
      eventPayload: {
        event: 'Clicked Element',
        properties: {
          name: action.name,
        },
      },
    }),
    [actionTypes.SEGMENT_TRACK]: (getState, action) => ({
      eventType: EventTypes.track,
      eventPayload: {
        event: action.name,
        properties: {
          ...action.extra,
        },
      },
    }),
    [actionTypes.SEGMENT_IDENTIFY]: (getState, action) => {
      const { traits } = action;
      return createIdentityEvent({
        userId: action.accountId,
        ...traits,
      });
    },
    [actionTypes.SESSION_SET_CURRENT_USER_ID]: (getState, action) => {
      return createIdentityEvent({
        userId: action.currentAccountId,
      });
    },
    [REHYDRATE]: (getState, action) => {
      /**
       * action will contain persisted store data
       * - ex. previous userId
       */
      const currentAccountId = action.payload?.session?.currentAccountId;

      /**
       * Do not send an $identify event if logged out
       */
      if (currentAccountId) {
        return createIdentityEvent({
          userId: currentAccountId,
        });
      }
    },
    // since the library currently doesn't reset correctly, we'll have to do it manually.
    [actionTypes.SESSION_CLEAR_CURRENT_USER_ID]: () => {
      if ((window as any).analytics && (window as any).analytics.reset) {
        (window as any).analytics.reset();
      }
    },
  },
};

const segmentMiddleware = createTracker(customMapper);

export default segmentMiddleware;
